import React, { SetStateAction, useContext, useEffect, useMemo, useRef, useState } from "react";
import FormModal2 from "../../../../components/form/FormModal2";
import { UserActivityDataTypes } from "../../../../types/user-activity-data-types";
import api, { apiV2 } from "../../../../api/axiosConfig";
import Modal from "../../../../components/portal/Modal";
import DoneIcon from "@mui/icons-material/Done";
import { UserContext, useAuth } from "../../../../context/user";
import { motion, AnimatePresence } from "framer-motion";

type MessageOption = {
  text: string;
  action: string;
};

type ChatMessage = {
  id?: string;
  message: string;
  sentAt?: string;
  sender: "App" | "Ops" | "Satellite" | "Siren";
  sentByName?: string;
  attachments?: MessageAttachment[];
  options?: MessageOption[];
  optionSelected?: number;
};

type MessageAttachment = {
  type?: string;
  url?: string;
  uri?: string;
  name?: string;
  thumbnailUrl?: string;
};

const normalizeAttachmentURL = (url?: string) => {
  if (!url) return "";
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return url.split("?")[0].split("#")[0];
  }
};

const attachmentKey = (attachments?: MessageAttachment[]) =>
  JSON.stringify(
    (attachments ?? []).map((attachment) => ({
      type: attachment.type ?? "",
      url: normalizeAttachmentURL(attachment.url ?? attachment.uri),
      name: attachment.name ?? "",
      thumbnailUrl: normalizeAttachmentURL(attachment.thumbnailUrl),
    }))
  );

const areMessagesEqual = (a: ChatMessage[], b: ChatMessage[]) => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (
      a[i].id !== b[i].id ||
      a[i].message !== b[i].message ||
      a[i].sentAt !== b[i].sentAt ||
      a[i].sender !== b[i].sender ||
      a[i].sentByName !== b[i].sentByName ||
      attachmentKey(a[i].attachments) !== attachmentKey(b[i].attachments) ||
      a[i].optionSelected !== b[i].optionSelected
    ) {
      return false;
    }
  }
  return true;
};

function Message({
  userID,
  deviceOID,
  source,
  firstName,
  userName,
  deviceName,
  deviceSourceAPI,
  setSendNotificationPopup,
}: {
  userID?: string,
  deviceOID?: string,
  source: string,
  firstName?: string,
  userName?:string,
  deviceName?: string,
  deviceSourceAPI?: string,
  setSendNotificationPopup: React.Dispatch<SetStateAction<boolean>>;
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const messageRef = useRef<HTMLTextAreaElement>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [hasUnreadWhileScrolledUp, setHasUnreadWhileScrolledUp] = useState(false);
  const [pendingAttachment, setPendingAttachment] = useState<File | null>(null);
  const [isSending, setIsSending] = useState(false);

  const pendingAttachmentPreview = useMemo(() => {
    if (!pendingAttachment) return null;
    return {
      file: pendingAttachment,
      previewURL: URL.createObjectURL(pendingAttachment),
      kind: pendingAttachment.type.startsWith("image/")
        ? "image"
        : pendingAttachment.type.startsWith("video/")
        ? "video"
        : "file",
    };
  }, [pendingAttachment]);

  const [userData] = useState(() => ({
    source,
    userID,
    deviceOID,
    metadata: {
      firstName,
      userName,
      deviceName,
      deviceSourceAPI,
    },
  }));

  useEffect(
    () => () => {
      if (pendingAttachmentPreview) {
        URL.revokeObjectURL(pendingAttachmentPreview.previewURL);
      }
    },
    [pendingAttachmentPreview]
  );

  useEffect(() => {
    const fetchMessages = () => {
      const queryParam =
        userData.source === "satellite"
          ? `deviceID=${userData.deviceOID}`
          : `userID=${userData.userID}`;

      apiV2
        .get(`get-message-history?${queryParam}`)
        .then((res) => {



          const nextMessages: ChatMessage[] = res.data.map((msg: any) => {
              const sender = (() => {

                  console.log("msg", msg.source, msg)
                  if (msg.source == "siren") {
                      return "Siren"
                  }

                  if( msg.metadata?.sentBy === userData.userID){
                      return "App"
                  }

                  if(msg.metadata?.sentBy === userData.deviceOID){
                      return "Satellite"
                  }

                  return "Ops"
              })()

              return {
                  id: msg.id,
                  message: msg.message,
                  sentAt: msg.sentAt,
                  sender: sender,
                  sentByName: msg.metadata?.sentByName,
                  attachments: msg.metadata?.attachments,
                  options: msg.options,
                  optionSelected: msg.optionSelected,
              }
          });
          setMessages((prev) =>
            areMessagesEqual(prev, nextMessages) ? prev : nextMessages
          );
        })
        .catch((err) => console.error("Failed to fetch message history:", err));
    };

    fetchMessages();
    const interval = setInterval(fetchMessages, 3000);

    return () => clearInterval(interval);
  }, [userData.source, userData.deviceOID, userData.userID]);

  useEffect(() => {
    if (messagesContainerRef.current && isInitialLoad && messages.length) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      setIsInitialLoad(false);
    }
  }, [messages, isInitialLoad]);

  const { clientName } = useAuth();

  const scrollToBottom = () => {
    if (!messagesContainerRef.current) return;
    messagesContainerRef.current.scrollTo({
      top: messagesContainerRef.current.scrollHeight,
      behavior: "smooth",
    });
    setShowScrollDown(false);
  };

  const handleMessagesScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const awayFromBottom = distanceFromBottom > 24;
    setShowScrollDown(awayFromBottom);
    if (!awayFromBottom) {
      setHasUnreadWhileScrolledUp(false);
    }
  };

  useEffect(() => {
    if (isInitialLoad || !messagesContainerRef.current) {
      return;
    }
    const el = messagesContainerRef.current;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom > 24) {
      setHasUnreadWhileScrolledUp(true);
    }
  }, [messages, isInitialLoad]);

  const handleAttachmentSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingAttachment(file);
    e.target.value = "";
  };

  const removePendingAttachment = () => {
    setPendingAttachment(null);
  };

  const handleMessage = async () => {
    const messageText = messageRef.current?.value.trim() ?? "";
    if (!messageText && !pendingAttachment) return;
    if (isSending) return;

    const endpoint =
      userData.source === "satellite"
        ? `ops/send-message/${userData.deviceOID}`
        : `ops/send-message/${userData.userID}`;

    setIsSending(true);
    try {
      let response;
      if (pendingAttachment) {
        const formData = new FormData();
        formData.append("message", messageText);
        if (userData.source === "satellite") {
          formData.append("toDevice", "true");
        }
        formData.append("attachments", pendingAttachment);
        response = await apiV2.post(endpoint, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        const payload: { message: string; toDevice?: boolean } = { message: messageText };
        if (userData.source === "satellite") payload.toDevice = true;
        response = await apiV2.post(endpoint, payload);
      }

      const newMsg = response.data.data;
      setMessages((prev) => [
        ...prev,
        {
          id: newMsg.id,
          message: newMsg.message ?? messageText,
          sender: "Ops",
          sentAt: newMsg.sentAt,
          sentByName: newMsg.sentByName,
          attachments: newMsg.metadata?.attachments,
        },
      ]);
      requestAnimationFrame(() => scrollToBottom());

      if (messageRef.current) {
        messageRef.current.value = "";
        messageRef.current.style.height = "auto";
      }
      setPendingAttachment(null);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSending(false);
    }
  };

  const openAttachment = (attachment?: MessageAttachment) => {
    const target = attachment?.url ?? attachment?.uri;
    if (target) {
      window.open(target, "_blank", "noopener,noreferrer");
    }
  };

  const userContext = useContext(UserContext);

  if (userContext?.sub == userData.userID) {
    return (
      <FormModal2 title="Send Message">
        <div className="flex flex-col gap-2 my-6">
          <div className="my-4">Message cannot be sent to self</div>
        </div>
      </FormModal2>
    );
  }

  const title =
    userData.source === "satellite"
      ? userData.metadata.deviceName ?? ""
      : userData.metadata.userName ?? "";

  return (
    <FormModal2 title={title}>
      <div className="relative w-[760px]">
        <div
          ref={messagesContainerRef}
          onScroll={handleMessagesScroll}
          className="flex flex-col gap-3 h-[28rem] overflow-y-auto bg-light-light-grey p-4 rounded-b-md"
        >
          <AnimatePresence initial={false}>
            {messages.map((msg, idx) => {
              const isSent = msg.sender === "Ops" || msg.sender == "Siren";
              const imageAttachments = (msg.attachments ?? []).filter((attachment) =>
                typeof attachment.type === "string"
                  ? attachment.type.startsWith("image")
                  : false
              );
              const videoAttachments = (msg.attachments ?? []).filter((attachment) =>
                typeof attachment.type === "string"
                  ? attachment.type.startsWith("video/")
                  : false
              );
              const fileAttachments = (msg.attachments ?? []).filter((attachment) =>
                typeof attachment.type === "string"
                  ? attachment.type.startsWith("application")
                  : false
              );

              console.log("Mesage", msg)
              return (
                <motion.div
                  key={msg.id ?? `${msg.sentAt}-${idx}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className={isSent ? "self-end" : "self-start"}
                >
                  <div
                    className={`rounded-xl px-4 py-2 max-w-xs shadow break-words whitespace-pre-wrap ${
                      isSent ? "bg-blue text-white" : "bg-white"
                    }`}
                  >
                    {imageAttachments.length > 0 && (
                      <div className="mb-2 grid grid-cols-2 gap-2">
                        {imageAttachments.map((attachment, imageIndex) => {
                          const src = attachment.url ?? attachment.uri;
                          if (!src) return null;
                          const stableSrc = normalizeAttachmentURL(src);
                          return (
                            <button
                              key={`${stableSrc}-${imageIndex}`}
                              type="button"
                              onClick={() => openAttachment(attachment)}
                              className="overflow-hidden rounded-lg border border-gray-200 bg-white/10"
                              title="Open image"
                            >
                              <img
                                src={src}
                                alt={attachment.name ?? `Attachment ${imageIndex + 1}`}
                                className="h-28 w-28 object-cover"
                              />
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {videoAttachments.length > 0 && (
                      <div className="mb-2 space-y-2">
                        {videoAttachments.map((attachment, videoIndex) => {
                          const src = attachment.url ?? attachment.uri;
                          if (!src) return null;
                          const thumb = attachment.thumbnailUrl;
                          return (
                            <button
                              key={`${normalizeAttachmentURL(src)}-${videoIndex}`}
                              type="button"
                              onClick={() => openAttachment(attachment)}
                              className="relative flex h-28 w-28 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-black/70"
                              title="Open video"
                            >
                              {thumb ? (
                                <img
                                  src={thumb}
                                  alt={attachment.name ?? `Video ${videoIndex + 1}`}
                                  className="absolute inset-0 h-full w-full object-cover opacity-80"
                                />
                              ) : null}
                              <span className="z-10 rounded-full bg-black/60 px-2 py-1 text-xs text-white">
                                Play
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {fileAttachments.length > 0 && (
                      <div className="mb-2 space-y-2">
                        {fileAttachments.map((attachment, fileIndex) => {
                          const src = attachment.url ?? attachment.uri;
                          if (!src) return null;
                          return (
                            <button
                              key={`${normalizeAttachmentURL(src)}-${fileIndex}`}
                              type="button"
                              onClick={() => openAttachment(attachment)}
                              className={`block w-full rounded-lg border px-3 py-2 text-left text-sm ${
                                isSent
                                  ? "border-blue-200 bg-blue-500/30 text-white"
                                  : "border-gray-200 bg-gray-100 text-gray-800"
                              }`}
                              title="Open file"
                            >
                              {attachment.name || "File attachment"}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {msg.message}
                  </div>
                  {msg.options && msg.options.length > 0 && (
                    <div className={`mt-1.5 flex flex-wrap gap-1.5 ${isSent ? "justify-end" : "justify-start"}`}>
                      {msg.options.map((option, optIdx) => {
                        const isSelected = msg.optionSelected === optIdx;
                        return (
                          <span
                            key={optIdx}
                            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                              isSelected
                                ? "border-blue bg-blue text-white"
                                : "border-gray-300 bg-white text-gray-500"
                            }`}
                          >
                            {isSelected && <DoneIcon style={{ fontSize: 10 }} />}
                            {option.text}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <div
                    className={`text-xs mt-1 ${
                      isSent ? "text-gray-300 text-right mr-2" : "text-gray-500 ml-2"
                    }`}
                  >
                    {(msg.sender === "Satellite" || msg.sender === "App" || msg.sender == "Siren")
                      ? msg.sender
                      : (msg.sentByName?.trim() || "")}
                    {" · "}
                    {msg.sentAt ? new Date(msg.sentAt).toLocaleString() : ""}
                  </div>
                </motion.div>
              );
            })}
            <div ref={messagesEndRef} />
          </AnimatePresence>
        </div>
        {showScrollDown && (
          <button
            type="button"
            onClick={scrollToBottom}
            className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 h-8 w-8 rounded-full bg-blue text-white text-base leading-none shadow-md hover:bg-opacity-90 transition flex items-center justify-center"
            aria-label="Scroll down"
            title="Scroll down"
          >
            ↓
            {hasUnreadWhileScrolledUp && (
              <>
                <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red border border-white" />
                <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red opacity-60 animate-ping" />
              </>
            )}
          </button>
        )}
      </div>


      {userData.metadata.deviceSourceAPI !== "everywhere-webhooks" && (
    <div className="mt-3 rounded-xl bg-light-light-grey p-3">
      {pendingAttachmentPreview && (
        <div className="mb-2 flex gap-2 overflow-x-auto pb-1">
            <div
              key={`${pendingAttachmentPreview.file.name}-${pendingAttachmentPreview.file.size}`}
              className="relative h-16 min-w-[64px] overflow-hidden rounded-lg border border-slate-200 bg-slate-100"
              title={pendingAttachmentPreview.file.name}
            >
              {pendingAttachmentPreview.kind === "image" && (
                <img
                  src={pendingAttachmentPreview.previewURL}
                  alt={pendingAttachmentPreview.file.name}
                  className="h-full w-full object-cover"
                />
              )}
              {pendingAttachmentPreview.kind === "video" && (
                <div className="relative h-full w-full">
                  <video
                    src={pendingAttachmentPreview.previewURL}
                    className="h-full w-full object-cover"
                    muted
                    preload="metadata"
                  />
                  <span className="absolute inset-x-0 bottom-0 bg-black/60 py-0.5 text-center text-[10px] font-medium text-white">
                    VIDEO
                  </span>
                </div>
              )}
              {pendingAttachmentPreview.kind === "file" && (
                <div className="flex h-full w-full items-center justify-center px-1 text-center text-[10px] font-medium text-slate-700">
                  {pendingAttachmentPreview.file.name}
                </div>
              )}
              <button
                type="button"
                className="absolute right-1 top-1 h-4 w-4 rounded-full bg-black/60 text-[10px] text-white hover:bg-black/80"
                onClick={removePendingAttachment}
                aria-label="Remove attachment"
                title="Remove attachment"
              >
                ×
              </button>
            </div>
        </div>
      )}
      <div className="flex items-stretch gap-2">
        {userData.source != "satellite" && (
          <button
            type="button"
            onClick={() => attachmentInputRef.current?.click()}
            className="inline-flex h-11 min-w-[128px] shrink-0 items-center justify-center rounded-lg bg-white border border-slate-300 px-3 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
            title="Attach files"
          >
            + Attach
          </button>
        )}
        <input
          ref={attachmentInputRef}
          type="file"
          className="hidden"
          onChange={handleAttachmentSelect}
        />
      <div className="flex-grow-[4] min-w-0">
        <textarea
          ref={messageRef}
          rows={1}
          className="w-full h-11 min-h-[44px] rounded-lg bg-white border border-slate-300 px-3 py-2 text-sm outline-none resize-y max-h-40 overflow-auto leading-relaxed focus:bg-white"
          placeholder="Type Message Here"
          onInput={(e) => {
            const el = e.currentTarget;
            el.style.height = "auto";
            el.style.height = `${el.scrollHeight}px`;
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleMessage();
            }
          }}
        />
      </div>
      <div className="flex-grow-[1] min-w-[150px]">
        <button
          type="button"
          onClick={handleMessage}
          disabled={isSending}
          className={`h-11 w-full rounded-lg px-4 text-sm font-semibold text-white shadow-sm transition ${
            isSending
              ? "cursor-not-allowed bg-slate-400"
              : "bg-blue hover:bg-[#005fbd]"
          }`}
        >
          {isSending
            ? "Sending..."
            : userData.source === "satellite"
            ? "Send to Device"
            : userData.source === "App" || userData.source === "app"
            ? "Send to App"
            : "Send"}
        </button>
      </div>
    </div>
  </div>
  )}
    </FormModal2>
  );
}

export default Message;
